// Static data for the Orbis "Partner With Us" page
import {
  FiMapPin,
  FiGlobe,
  FiEdit3,
  FiHeadphones,
  FiStar,
  FiShield,
  FiUsers,
  FiTrendingUp,
  FiAward,
  FiClock,
  FiBriefcase,
  FiCompass,
  FiHome,
  FiCalendar,
  FiUserCheck,
  FiSend,
  FiPhoneCall,
  FiFileText,
  FiCheckCircle,
  FiRepeat,
  FiHeart,
} from 'react-icons/fi';

export const BREADCRUMB = {
  home: '/',
  current: 'Partner With Us',
};

export const HERO = {
  image:
    'https://images.unsplash.com/photo-1517840901100-8179e982acb7?q=80&w=1920&auto=format&fit=crop',
  eyebrow: 'Partner With Orbis',
  heading: 'Grow Further, Together.',
  subheading:
    "We don't just manage travel — we create strategic travel solutions that drive business success, strengthen partnerships, and deliver memorable experiences.",
  cta: { label: 'Become a Partner', href: '#partner-form' },
};

/* Long-form "Why Us" narrative supplied by the client, broken into themed blocks */
export const WHY_US_INTRO = {
  eyebrow: 'Why Us',
  heading: 'Travel Solutions Built on Expertise & Trust',
  paragraph:
    "At Orbis, we don't just manage travel — we create strategic travel solutions that drive business success, strengthen partnerships, and deliver memorable experiences. Every journey we design is backed by expertise, innovation, and an unwavering commitment to excellence.",
};

export const WHY_US_BLOCKS = [
  {
    id: 'global-network',
    icon: FiGlobe,
    title: 'A Global Network You Can Trust',
    paragraph:
      'Our strength lies in the relationships we build. Through a carefully curated network of trusted destination management companies, hotels, venues, airlines, transport providers, and local experts across the world, we ensure every client benefits from dependable service, competitive value, and seamless execution.',
  },
  {
    id: 'expertise',
    icon: FiCompass,
    title: 'Expertise Across Diverse Travel Verticals',
    paragraph:
      'Our team brings specialized knowledge across multiple travel verticals, including Corporate Travel, MICE, Destination Weddings, Destination Management, B2B Travel Solutions, Luxury Experiences, and Group Travel — delivering tailored solutions while maintaining the highest standards of planning and execution.',
  },
  {
    id: 'customer-centric',
    icon: FiUsers,
    title: 'Customer-Centric by Design',
    paragraph:
      "We take the time to understand our clients' objectives, preferences, budgets, and expectations before designing customized solutions — never standardized packages. Our relationship-first approach ensures personalized service and dedicated support from planning through execution.",
  },
  {
    id: 'operational-excellence',
    icon: FiCheckCircle,
    title: 'Operational Excellence That Delivers Confidence',
    paragraph:
      'Our operational framework combines detailed planning, supplier coordination, risk management, and on-ground execution. Whether managing executive travel or a destination wedding with hundreds of guests, our commitment remains the same — flawless execution with attention to every detail.',
  },
  {
    id: 'trust-transparency',
    icon: FiShield,
    title: 'Built on Trust, Transparency & Long-Term Partnerships',
    paragraph:
      'Transparency in communication, ethical business practices, reliable execution, and accountability form the foundation of every relationship we build. We value long-term partnerships over short-term transactions.',
  },
  {
    id: 'innovation',
    icon: FiTrendingUp,
    title: 'Powered by Innovation & Technology',
    paragraph:
      'Orbis embraces technology to enhance efficiency, improve customer experiences, streamline operations, and provide greater visibility across every stage of the travel journey — combining digital capabilities with human expertise.',
  },
  {
    id: 'sustainable-growth',
    icon: FiRepeat,
    title: 'Committed to Sustainable Growth',
    paragraph:
      'We are committed to building a business that is responsible, scalable, and future-ready — creating sustainable growth for our clients, employees, suppliers, and the communities we engage with.',
  },
];

export const ORBIS_DIFFERENCE = {
  heading: 'The Orbis Difference',
  paragraph:
    'At Orbis, we combine global reach with local expertise, strategic thinking with flawless execution, and innovation with genuine human relationships. This unique combination enables us to deliver travel solutions that go beyond expectations — creating value for every client, every partner, and every journey.',
};

/* Short feature cards specifically requested for "Why Partner With Orbis" */
export const PARTNER_FEATURES = [
  {
    id: 'local-expertise',
    icon: FiMapPin,
    title: 'Local Expertise',
    description: 'On-ground teams who know every destination inside out.',
  },
  {
    id: 'trusted-network',
    icon: FiGlobe,
    title: 'Trusted Network',
    description: 'A curated network of hotels, DMCs, and suppliers worldwide.',
  },
  {
    id: 'tailor-made',
    icon: FiEdit3,
    title: 'Tailor-Made Solutions',
    description: 'No templated packages — every program built around your needs.',
  },
  {
    id: 'dedicated-support',
    icon: FiHeadphones,
    title: 'Dedicated Support',
    description: 'A named point of contact throughout every partnership.',
  },
  {
    id: 'luxury-experiences',
    icon: FiStar,
    title: 'Luxury Experiences',
    description: 'Five-star standards across every touchpoint of the journey.',
  },
  {
    id: 'transparent-collaboration',
    icon: FiShield,
    title: 'Transparent Collaboration',
    description: 'Clear pricing and honest communication at every stage.',
  },
];

export const PROCESS_STEPS = [
  {
    id: 1,
    icon: FiPhoneCall,
    title: 'Initial Consultation',
    description: 'We learn about your business, your clients, and your goals.',
  },
  {
    id: 2,
    icon: FiFileText,
    title: 'Proposal & Agreement',
    description: 'A tailored partnership proposal with transparent terms.',
  },
  {
    id: 3,
    icon: FiUserCheck,
    title: 'Onboarding',
    description: 'Dedicated account setup and a named point of contact.',
  },
  {
    id: 4,
    icon: FiCompass,
    title: 'Program Design & Execution',
    description: 'Itineraries and logistics built, tested, and delivered.',
  },
  {
    id: 5,
    icon: FiTrendingUp,
    title: 'Ongoing Support & Growth',
    description: 'Continuous optimization as the partnership scales.',
  },
];

export const PARTNER_TYPES = [
  { id: 'travel-agencies', icon: FiBriefcase, title: 'Travel Agencies' },
  { id: 'tour-operators', icon: FiCompass, title: 'Tour Operators' },
  { id: 'corporate-clients', icon: FiUsers, title: 'Corporate Clients' },
  { id: 'event-planners', icon: FiCalendar, title: 'Event Planners' },
  { id: 'luxury-advisors', icon: FiStar, title: 'Luxury Travel Advisors' },
  { id: 'hotels', icon: FiHome, title: 'Hotels' },
  { id: 'dmc-partners', icon: FiGlobe, title: 'DMC Partners' },
];

export const TRUST_STATS = [
  { id: 'years', icon: FiAward, value: 10, suffix: '+', label: 'Years Experience' },
  { id: 'travelers', icon: FiUsers, value: 5000, suffix: '+', label: 'Happy Travelers' },
  { id: 'partners', icon: FiGlobe, value: 100, suffix: '+', label: 'Global Partners' },
  { id: 'support', icon: FiClock, value: 24, suffix: '/7', label: 'Support', isStatic: true },
];

export const TESTIMONIALS = [
  {
    id: 1,
    name: 'Rahul Sethi',
    role: 'Director, Skyline Travel Agency',
    rating: 5,
    review:
      'Orbis has become an extension of our own team. Their responsiveness and destination knowledge have helped us win business we couldn\'t have handled alone.',
  },
  {
    id: 2,
    name: 'Claire Dubois',
    role: 'Founder, Dubois Event Co.',
    rating: 5,
    review:
      'From proposal to execution, every step was transparent. Our clients consistently rate the on-ground experience as the highlight of their trip.',
  },
  {
    id: 3,
    name: 'Michael Tan',
    role: 'GM, Regency Hotels Group',
    rating: 5,
    review:
      'A dependable B2B partner with genuinely global reach. Communication is fast and the quality of execution never wavers.',
  },
];

export const TRUSTED_PARTNERS = [
  '500+ Global Suppliers',
  'IATA Accredited',
  '40+ Destination Network',
  '100+ Global Partners',
  '24/7 Partner Support',
];

export const FAQS = [
  {
    id: 1,
    question: 'Who can become an Orbis partner?',
    answer:
      'Travel agencies, tour operators, corporate clients, event planners, luxury travel advisors, hotels, and DMC partners are all welcome to apply for a partnership with Orbis.',
  },
  {
    id: 2,
    question: 'Is there a minimum volume requirement to partner with Orbis?',
    answer:
      'No fixed minimum — we work with partners at different stages of growth and tailor the collaboration model to fit your business volume and goals.',
  },
  {
    id: 3,
    question: 'What destinations does Orbis operate in?',
    answer:
      'Our network spans 40+ destinations worldwide, supported by local DMC partners and on-ground teams in key markets.',
  },
  {
    id: 4,
    question: 'How long does onboarding take?',
    answer:
      'Most partners are fully onboarded — with a dedicated point of contact and agreed pricing — within 1 to 2 weeks of signing.',
  },
  {
    id: 5,
    question: 'Does Orbis offer white-label solutions?',
    answer:
      'Yes, white-label options are available for travel agencies and advisors who want to offer Orbis-built itineraries under their own brand.',
  },
];

export const FORM_FIELDS = [
  { id: 'name', label: 'Name', type: 'text', required: true },
  { id: 'company', label: 'Company', type: 'text', required: true },
  { id: 'email', label: 'Email', type: 'email', required: true },
  { id: 'phone', label: 'Phone', type: 'tel', required: true },
  { id: 'country', label: 'Country', type: 'text', required: true },
];

export const BUSINESS_TYPE_OPTIONS = [
  'Travel Agency',
  'Tour Operator',
  'Corporate Client',
  'Event Planner',
  'Luxury Travel Advisor',
  'Hotel',
  'DMC Partner',
  'Other',
];

export const FORM_CONTENT = {
  eyebrow: 'Get In Touch',
  heading: 'Start the Conversation',
  subheading: 'Tell us about your business and we\'ll be in touch within one business day.',
  submitLabel: 'Become Our Partner',
};

export const WHAT_WE_DO = {
  eyebrow: 'Our Expertise',
  heading: 'Experiences We Create',
  items: [
    {
      id: 'corporate-travel',
      icon: FiBriefcase,
      title: 'Corporate Travel',
      description:
        'Comprehensive travel management solutions designed to simplify business travel while enhancing efficiency, cost control and traveler experience.',
    },
    {
      id: 'mice',
      icon: FiUsers,
      title: 'MICE (Meetings, Incentives, Conferences & Exhibitions)',
      description:
        'End-to-end planning and execution of meetings, incentive programs, conferences, product launches, and corporate events across leading global destinations.',
    },
    {
      id: 'destination-weddings',
      icon: FiHeart,
      title: 'Destination Weddings',
      description:
        'Curated destination wedding experiences that combine creativity, meticulous planning, and flawless execution to create celebrations that are truly unforgettable.',
    },
    {
      id: 'dmc',
      icon: FiGlobe,
      title: 'Destination Management (DMC)',
      description:
        'Local expertise with global standards, providing destination services, logistics, contracting, excursions, transportation, and on-ground support for leisure and business travel.',
    },
    {
      id: 'b2b',
      icon: FiTrendingUp,
      title: 'B2B Travel Solutions',
      description:
        'Strategic partnerships with travel agencies, tour operators, and corporate clients, offering reliable products, destination expertise, and scalable travel solutions.',
    },
  ],
};

export const AOS_CONFIG = {
  duration: 800,
  easing: 'ease-out-cubic',
  once: true,
  offset: 60,
};